// @givmo/app-admin — employer-entered OCCASION DATES: formatting, draft validation, and the
// write-body builder for the Roster tab (EP-WEB11).
//
// ★ D-EP-27 — the one thing to get right in this file. These dates are the EMPLOYER's entry
// (`company_employee.birthday_month` / `.birthday_day` / `.hire_date`), not the employee's profile
// birthday: the CONSUMER `birth_date`, a different fact on a different basis that the employer must
// never see. The roster row no longer carries it (Company Profiles' CAM-R141 made
// `CompanyEmployeeOut` an allowlist; their CAM-R170 may restore it), though the committed snapshot
// declares it until its next regen. Nothing here reads it, and this package's
// `eslint.config.mjs` (as `apps/admin`'s) fails the lint if anything ever does.
//
// TWO consequences of "month + day, no year" that shape every function below:
//   - A birthday is NEVER a `Date`. Constructing one needs a year, and the backend REFUSES a
//     year-carrying value rather than truncating it — so month/day are formatted from a fixed
//     name table and never round-trip through `Date`.
//   - `hire_date` IS a full date, but it is formatted from its `YYYY-MM-DD` STRING parts too:
//     `new Date('2024-01-05')` parses as UTC midnight and renders as Jan 4 anywhere west of UTC.

import { firstErrorCode, isGivmoError } from '@givmo/api-client';
import type {
  CompanyEmployeeOut,
  EmployeeOccasionDatesImportOut,
  EmployeeOccasionDatesImportRowOut,
  EmployeeOccasionDatesSet,
} from '@givmo/api-client';

// ── The CSV contract, mirrored from the backend (none of it is guessable) ───────────────────────
// `app/domain/entities/company_occasion_entities.py` — `parse_import_birthday` / `parse_import_hire_date`.

/** The header row the import requires, EXACTLY. A different header is a structural 400. */
export const CSV_HEADER = 'work_email,birthday,hire_date';

/** The literal cell value that CLEARS a stored date — as distinct from a blank cell, which leaves
 *  it alone. Two intents, two spellings; an employer with no way to say "remove this" has to ask
 *  support, so the copy must state both. */
export const CSV_CLEAR_TOKEN = '-';

/** Structural cap: more rows than this is a 400, not a partial apply. */
export const CSV_MAX_ROWS = 10_000;

/** The import applies row by row, so a 2xx's counts are a receipt for what LANDED — never a claim
 *  that the file succeeded or failed as a whole. */
export const IMPORT_APPLIED_NOTE =
  'Rows are applied one at a time, so these counts describe exactly what landed — not an all-or-nothing upload.';

/** Recovery for a request that died mid-file: the rows already applied are durable and there is no
 *  rollback, but every write is idempotent, so the same file converges instead of double-applying. */
export const IMPORT_RETRY_NOTE =
  'Some rows may already have been applied. Re-upload the same file — every write is idempotent, so rows that already landed come back as “unchanged”.';

/** What the surface says when the backend occasion routes are dark in this environment. */
export const OCCASION_UNAVAILABLE_NOTE =
  'Occasion dates are not available in this environment yet. Nothing was saved, and nothing is wrong with your file — the feature simply is not switched on here.';

// ── Calendar tables (no `Date`, no locale) ─────────────────────────────────────────────────────

const MONTH_LONG = [
  '',
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const MONTH_SHORT = [
  '',
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/** Days per month for a BIRTHDAY. February is 29 because Feb 29 is a real birthday — the backend
 *  accepts it and clamps the occurrence to Feb 28 in non-leap years (`is_valid_month_day`). */
const BIRTHDAY_MAX_DAY = [0, 31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/** Earliest hire year the backend accepts; older is a column mix-up, not an employment fact. */
export const HIRE_DATE_MIN_YEAR = 1900;

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

function daysInMonth(year: number, month: number): number {
  if (month === 2) return isLeapYear(year) ? 29 : 28;
  return BIRTHDAY_MAX_DAY[month] ?? 0;
}

export interface OccasionSelectOption {
  value: string;
  label: string;
}

/** Month options for the birthday picker, plus the "not set" row that CLEARS the pair. */
export function birthdayMonthOptions(): OccasionSelectOption[] {
  return [
    { value: '', label: 'Not set' },
    ...MONTH_LONG.slice(1).map((label, index) => ({ value: String(index + 1), label })),
  ];
}

/** Day options for the picked month. Bounded by that month's real length so the picker cannot
 *  compose Feb 30 — the value the backend rejects with the same 422 as "month 13". */
export function birthdayDayOptions(month: string): OccasionSelectOption[] {
  const parsed = Number.parseInt(month, 10);
  const max = Number.isNaN(parsed) ? 31 : (BIRTHDAY_MAX_DAY[parsed] ?? 31);
  return [
    { value: '', label: 'Not set' },
    ...Array.from({ length: max }, (_, i) => ({ value: String(i + 1), label: String(i + 1) })),
  ];
}

// ── Display ────────────────────────────────────────────────────────────────────────────────────

/** `Mar 14` — the roster-column form. Null when either half is missing (half a birthday is not a
 *  date, and the wire can only carry the pair). */
export function formatBirthdayShort(
  month: number | null | undefined,
  day: number | null | undefined,
): string | null {
  if (month == null || day == null) return null;
  const name = MONTH_SHORT[month];
  if (name === undefined || name === '') return null;
  return `${name} ${day}`;
}

/** `March 14` — the accessible/long form for labels and dialog copy. */
export function formatBirthdayLong(
  month: number | null | undefined,
  day: number | null | undefined,
): string | null {
  if (month == null || day == null) return null;
  const name = MONTH_LONG[month];
  if (name === undefined || name === '') return null;
  return `${name} ${day}`;
}

function splitIsoDate(iso: string | null | undefined): [number, number, number] | null {
  if (typeof iso !== 'string') return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (match === null) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > daysInMonth(year, month)) return null;
  return [year, month, day];
}

/** `Jan 5, 2024` — parsed from the ISO string's parts, never through `Date` (a UTC-midnight parse
 *  renders the previous day for anyone west of UTC). */
export function formatHireDateShort(iso: string | null | undefined): string | null {
  const parts = splitIsoDate(iso);
  if (parts === null) return null;
  const [year, month, day] = parts;
  return `${MONTH_SHORT[month]} ${day}, ${year}`;
}

/** `January 5, 2024`. */
export function formatHireDateLong(iso: string | null | undefined): string | null {
  const parts = splitIsoDate(iso);
  if (parts === null) return null;
  const [year, month, day] = parts;
  return `${MONTH_LONG[month]} ${day}, ${year}`;
}

/** The Roster "Dates on file" cell: the two short forms, each null when absent. */
export function occasionDatesCell(employee: CompanyEmployeeOut): {
  birthday: string | null;
  hireDate: string | null;
} {
  return {
    birthday: formatBirthdayShort(employee.birthday_month, employee.birthday_day),
    hireDate: formatHireDateShort(employee.hire_date),
  };
}

// ── The per-employee dialog's draft ────────────────────────────────────────────────────────────

export interface OccasionDatesDraft {
  /** `''` = not set. Stringly-typed because it is bound straight to a <Select>. */
  birthdayMonth: string;
  birthdayDay: string;
  /** `YYYY-MM-DD`, or `''` = not set. */
  hireDate: string;
}

/**
 * Seed the dialog from the ROSTER ROW. EP-BE11 put `birthday_month` / `birthday_day` / `hire_date`
 * on `CompanyEmployeeOut` precisely so the dialog pre-fills from the list the page already has —
 * there is no occasion-dates GET, and adding one would make the column an N+1.
 */
export function occasionDraftFromEmployee(employee: CompanyEmployeeOut): OccasionDatesDraft {
  return {
    birthdayMonth: employee.birthday_month == null ? '' : String(employee.birthday_month),
    birthdayDay: employee.birthday_day == null ? '' : String(employee.birthday_day),
    hireDate: typeof employee.hire_date === 'string' ? employee.hire_date : '',
  };
}

/** The latest hire date the backend accepts: today's month/day, one year out. */
export function maxHireDate(today: Date): string {
  const year = today.getFullYear() + 1;
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export const MIN_HIRE_DATE = `${HIRE_DATE_MIN_YEAR}-01-01`;

/**
 * Mirror of the backend's hire-date sanity bounds (`parse_import_hire_date`): a real `YYYY-MM-DD`,
 * year ≥ 1900, and no more than one year in the future. ISO dates compare correctly as strings, so
 * the bounds are plain comparisons — no `Date` arithmetic, no timezone.
 */
export function hireDateError(value: string, today: Date): string | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parts = splitIsoDate(trimmed);
  if (parts === null) return 'Enter the hire date as YYYY-MM-DD.';
  if (parts[0] < HIRE_DATE_MIN_YEAR) return `Hire date must be ${HIRE_DATE_MIN_YEAR} or later.`;
  if (trimmed > maxHireDate(today)) return 'Hire date can’t be more than a year from now.';
  return null;
}

/**
 * Why the draft cannot be saved yet, or null. The month/day pair is the only cross-field rule:
 * the wire requires them to be supplied or cleared TOGETHER, so a half-picked birthday is blocked
 * here rather than silently sent as a CLEAR the admin did not ask for.
 */
export function occasionDraftError(draft: OccasionDatesDraft, today: Date): string | null {
  const monthSet = draft.birthdayMonth !== '';
  const daySet = draft.birthdayDay !== '';
  if (monthSet !== daySet) {
    return 'Pick both a month and a day — Givmo stores a birthday as a month and day, never a year.';
  }
  return hireDateError(draft.hireDate, today);
}

/**
 * The PUT body — ONLY what changed. Absent-vs-null is load-bearing on this route: an ABSENT key
 * leaves the stored value alone, an explicit `null` CLEARS it. So a key is written only when the
 * draft differs from what the row already carries, and the month/day pair always travels together.
 *
 * Returns `null` when nothing changed, so the caller can disable Save instead of sending `{}`.
 * Assumes a draft that passed `occasionDraftError` (both-or-neither on the pair).
 */
export function buildOccasionDatesBody(
  initial: OccasionDatesDraft,
  draft: OccasionDatesDraft,
): EmployeeOccasionDatesSet | null {
  const body: EmployeeOccasionDatesSet = {};
  let changed = false;

  if (draft.birthdayMonth !== initial.birthdayMonth || draft.birthdayDay !== initial.birthdayDay) {
    changed = true;
    if (draft.birthdayMonth === '' || draft.birthdayDay === '') {
      body.birthday_month = null;
      body.birthday_day = null;
    } else {
      body.birthday_month = Number(draft.birthdayMonth);
      body.birthday_day = Number(draft.birthdayDay);
    }
  }

  if (draft.hireDate !== initial.hireDate) {
    changed = true;
    body.hire_date = draft.hireDate === '' ? null : draft.hireDate;
  }

  return changed ? body : null;
}

// ── The import echo ────────────────────────────────────────────────────────────────────────────

/** Per-row outcomes, in the order the counts strip reads them. */
const IMPORT_OUTCOMES = ['created', 'updated', 'unchanged', 'unmatched', 'invalid'] as const;
export type ImportOutcome = (typeof IMPORT_OUTCOMES)[number];

const OUTCOME_LABEL: Record<ImportOutcome, string> = {
  created: 'Created',
  updated: 'Updated',
  unchanged: 'Unchanged',
  unmatched: 'Not on your roster',
  invalid: 'Couldn’t be read',
};

const OUTCOME_TONE: Record<ImportOutcome, 'success' | 'neutral' | 'warning' | 'danger'> = {
  created: 'success',
  updated: 'success',
  unchanged: 'neutral',
  unmatched: 'warning',
  invalid: 'danger',
};

function asOutcome(value: string): ImportOutcome | null {
  return (IMPORT_OUTCOMES as readonly string[]).includes(value) ? (value as ImportOutcome) : null;
}

/** `outcome` is a bare `str` on the wire, so an unknown value renders verbatim rather than blank. */
export function importOutcomeLabel(outcome: string): string {
  const known = asOutcome(outcome);
  return known === null ? outcome : OUTCOME_LABEL[known];
}

export function importOutcomeTone(outcome: string): 'success' | 'neutral' | 'warning' | 'danger' {
  const known = asOutcome(outcome);
  return known === null ? 'neutral' : OUTCOME_TONE[known];
}

/** The five counts, in wire order, for the summary strip. */
export function importCountsView(
  result: EmployeeOccasionDatesImportOut,
): { outcome: ImportOutcome; label: string; count: number }[] {
  return IMPORT_OUTCOMES.map((outcome) => ({
    outcome,
    label: OUTCOME_LABEL[outcome],
    count: result[outcome] ?? 0,
  }));
}

/** Headline for a 2xx: what was READ and what LANDED. Never "succeeded" / "failed". */
export function importSummaryLine(result: EmployeeOccasionDatesImportOut): string {
  const total = result.total_rows ?? 0;
  const applied = (result.created ?? 0) + (result.updated ?? 0);
  const rows = total === 1 ? 'row' : 'rows';
  return `${total} ${rows} read — ${applied} changed a date on your roster.`;
}

/** A row whose outcome the admin has to act on (fix the spreadsheet, or add the person). */
export function importRowNeedsAttention(row: EmployeeOccasionDatesImportRowOut): boolean {
  return row.outcome === 'unmatched' || row.outcome === 'invalid';
}

/**
 * Human copy for the reason tokens the backend emits as MACHINE CODES rather than sentences:
 * `not_on_roster` on every `unmatched` row (`company_occasion_program_service.py:364`) and
 * `missing_work_email` on a row whose first cell is blank (`:570`, which lands as `invalid`).
 * Rendering `reason` verbatim would put snake_case in front of an admin.
 *
 * The parser's OWN reasons are already sentences ("birthday must be MM-DD with no year …") and pass
 * through untouched.
 */
const REASON_COPY: Record<string, string> = {
  not_on_roster: 'No one with this work email is on your roster.',
  missing_work_email: 'This row has no work email, so it could not be matched to anyone.',
};

/** A bare machine token: lowercase words joined by underscores, no spaces or punctuation. */
const MACHINE_TOKEN = /^[a-z0-9]+(?:_[a-z0-9]+)+$/;

/**
 * The reason to show for one echoed row, or null when there is none. An unrecognised token added by
 * a later backend is humanized generically rather than leaked raw — a new code should read as an
 * awkward sentence, never as `some_new_token`.
 */
export function importRowReason(reason: string | null | undefined): string | null {
  const value = (reason ?? '').trim();
  if (value === '') return null;
  const known = REASON_COPY[value];
  if (known !== undefined) return known;
  if (MACHINE_TOKEN.test(value)) {
    const words = value.replace(/_/g, ' ');
    return `${words.charAt(0).toUpperCase()}${words.slice(1)}.`;
  }
  return value;
}

/** How many echo rows the dialog renders. A 10,000-row file is legal, and a 10,000-node list is
 *  not a usable dialog — so the list is capped and says so, while the counts always cover the
 *  whole file. Attention-first ordering puts the ACTIONABLE rows at the front, so they are the LAST
 *  to be dropped — but it is not a guarantee: a file with more than `limit` unmatched/invalid rows
 *  still truncates some of them, and only the counts describe the whole file. Row numbers stay
 *  verbatim (1-based INCLUDING the header) so they match the spreadsheet. */
export const IMPORT_ECHO_LIMIT = 200;

export function orderedImportRows(
  rows: EmployeeOccasionDatesImportRowOut[] | null | undefined,
  limit: number = IMPORT_ECHO_LIMIT,
): { visible: EmployeeOccasionDatesImportRowOut[]; hiddenCount: number } {
  const all = Array.isArray(rows) ? rows : [];
  const attention = all.filter(importRowNeedsAttention);
  const rest = all.filter((row) => !importRowNeedsAttention(row));
  const ordered = [...attention, ...rest];
  return {
    visible: ordered.slice(0, limit),
    hiddenCount: Math.max(0, ordered.length - limit),
  };
}

// ── Error classification ───────────────────────────────────────────────────────────────────────

/**
 * The FLAG-SKEW case, not a failure. `COMPANY_OCCASION_PROGRAMS_ENABLED` gates the backend routes
 * independently of this build's `VITE_COMPANY_OCCASION_PROGRAMS_ENABLED`, so a 404 can mean "not
 * switched on in this environment" and must degrade to a considered notice — never an error toast,
 * which would read as "your file was rejected".
 *
 * ⚠ SAFE ONLY FOR THE IMPORT ROUTE. `POST …/occasion-dates/import` has no path entity, so its 404
 * can only be flag-off or a missing route. The PUT takes an `{employee_user_id}` and 404s for TWO
 * different reasons — use {@link isEmployeeOffRoster} to tell them apart before latching.
 */
export function isOccasionRouteUnavailable(error: unknown): boolean {
  return isGivmoError(error) && error.status === 404;
}

/**
 * The roster-miss 404, as distinct from the flag-off 404 — both arrive on
 * `PUT /admin/company/employees/{id}/occasion-dates` with the same status:
 *
 *   - flag off  → "Company occasion programs are not available"
 *     (`_require_company_occasion_programs_enabled`, `app/api/routers/companies.py:313`)
 *   - not on the roster → "That employee is not on your company roster"
 *     (`CompanyOccasionProgramService.set_employee_occasion_dates`,
 *     `app/application/company_occasion_program_service.py:271`)
 *   - route absent entirely (an older deployment) → FastAPI's plain "Not Found"
 *
 * Only the roster miss is about THIS employee; the other two are about the environment. Getting it
 * backwards is user-visible in both directions — latching on a roster miss would tell an admin the
 * whole feature is off, and not latching on flag-off would tell them a present employee is missing.
 *
 * ★ THE BRANCH KEY IS THE `error_code`, and the ASYMMETRY IS THE CONTRACT. EP-BE12 gave the roster miss
 * a stable `EMPLOYEE_NOT_ON_ROSTER` and deliberately left the flag-off route 404 code-LESS, so the two
 * are now told apart by a fixed-vocabulary token rather than by English copy — which is what the
 * api-client's own guidance asks for, and what the previous message match could not do safely.
 *
 * The message match SURVIVES AS A FALLBACK, and must not be removed yet. This build reaches staging and
 * prod before the backend carrying that code does, and against an older backend the roster miss arrives
 * with the same 404 and the same English and no code at all — dropping the fallback would silently
 * reclassify it as flag-off and tell the admin the whole feature is switched off. So the reader is
 * additive-tolerant in both directions: code present and matching wins outright (whatever the message
 * says, since server copy may be reworded at any time); no code falls back to the clause.
 *
 * It still FAILS SAFE. An unrecognised 404 — no code, no known clause — falls through to the
 * environment reading, which is the conservative one: it stops further doomed writes rather than
 * blaming an employee who may well be on the roster.
 */
const OFF_ROSTER_CODE = 'EMPLOYEE_NOT_ON_ROSTER';
const OFF_ROSTER_CLAUSE = 'not on your company roster';

export function isEmployeeOffRoster(error: unknown): boolean {
  if (!isGivmoError(error) || error.status !== 404) return false;
  const code = firstErrorCode(error);
  // A code the backend DID send settles it either way — an unrecognised one is some other 404 on this
  // route, and re-reading its message would be second-guessing a machine answer with a human one.
  if (code != null && code.length > 0) return code === OFF_ROSTER_CODE;
  return error.message.toLowerCase().includes(OFF_ROSTER_CLAUSE);
}

/** What the dialog says when the employee is gone from the roster mid-edit. */
export const OFF_ROSTER_NOTE =
  'That employee is no longer on your company roster, so their dates were not saved. The roster has been refreshed.';

/**
 * A PRE-APPLY rejection of the whole upload — the backend read the body and applied NOTHING, so it
 * is fatal-but-retryable: fix the file and upload again. Two statuses, one meaning:
 *
 *   - **400** — structural, raised while parsing: wrong header, no data rows, or more than
 *     10,000 rows (`ValidationException` in `_parse_import_rows`).
 *   - **422** — schema, raised by Pydantic BEFORE the handler body runs: `csv` is
 *     `Field(min_length=1, max_length=1_000_000)`, and a legitimate-looking large export
 *     (10,000 rows × ~100 chars is already over 1 MB) trips the max.
 *
 * A 422 on THIS route can only be that: every per-row problem rides a 2xx as an `invalid` row.
 * Classifying it as a mid-flight failure instead would tell the admin rows "may already have been
 * applied" when none were, and offer a Re-upload that loops straight back into the same 422.
 *
 * ⚠ IMPORT ONLY. The PUT route uses 422 for a malformed date — a real, correctable entry error that
 * must stay an inline field error. Do not reuse this predicate there.
 */
export function isStructuralImportError(error: unknown): boolean {
  return isGivmoError(error) && (error.status === 400 || error.status === 422);
}

/** The body-size ceiling on the import (`EmployeeOccasionDatesImport.csv`), in characters. */
export const CSV_MAX_CHARS = 1_000_000;
